import { useCallback, useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import axios from 'axios';
import { Check, HelpCircle, X, Camera, ArrowLeft, Flame } from 'lucide-react';
import { API_URL } from '../utils/api';
import MilestoneCelebration from '../components/MilestoneCelebration';
import ShareButton from '../components/ShareButton';
import FavoriteButton from '../components/FavoriteButton';
import logger from '../utils/logger';

const verdictConfig = {
  'GO': {
    label: 'GO',
    flashClass: 'verdict-flash-go',
    color: '#00E676',
    textColor: 'text-qc-bg',
    accent: 'text-qc-green',
    bgSoft: 'bg-qc-green/10',
    borderSoft: 'border-qc-green/30',
    Icon: Check,
    fallbackSuggestion: 'Keep stocking up on simple, recognizable ingredients.'
  },
  'MAYBE': {
    label: 'MAYBE',
    flashClass: 'verdict-flash-maybe',
    color: '#FFC107',
    textColor: 'text-qc-bg',
    accent: 'text-qc-amber',
    bgSoft: 'bg-qc-amber/10',
    borderSoft: 'border-qc-amber/30',
    Icon: HelpCircle,
    fallbackSuggestion: 'Look for a version with fewer additives or lower added sugar.'
  },
  'HELL NO': {
    label: 'HELL NO',
    flashClass: 'verdict-flash-no',
    color: '#FF1744',
    textColor: 'text-qc-text',
    accent: 'text-qc-red',
    bgSoft: 'bg-qc-red/10',
    borderSoft: 'border-qc-red/30',
    Icon: X,
    fallbackSuggestion: 'Look for a whole-food alternative with a short ingredient list.'
  }
};

export default function Result() {
  const { scanId } = useParams();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showFlash, setShowFlash] = useState(true);
  const [milestoneData, setMilestoneData] = useState(null);
  const [showMilestone, setShowMilestone] = useState(false);
  const { token } = useAuth();
  const navigate = useNavigate();

  // Pull stashed milestone/streak (from Scan.js sessionStorage handoff)
  useEffect(() => {
    if (!scanId) return;
    try {
      const raw = sessionStorage.getItem(`qc:milestone:${scanId}`);
      if (!raw) return;
      const parsed = JSON.parse(raw);
      setMilestoneData(parsed);
      if (parsed?.milestone) setShowMilestone(true);
      sessionStorage.removeItem(`qc:milestone:${scanId}`);
    } catch (e) {
      logger.warn('Failed to read stashed milestone data:', e);
    }
  }, [scanId]);

  const loadScanResult = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/scan/${scanId}`, {
        withCredentials: true
      });
      setScan(response.data);
    } catch (error) {
      logger.error('Failed to load scan result:', error);
    } finally {
      setLoading(false);
    }
  }, [scanId]);

  useEffect(() => {
    loadScanResult();
  }, [loadScanResult]);

  // Memoize the share payload to prevent unnecessary re-renders
  const sharePayload = useMemo(() => {
    if (!scan) return null;
    return { scanId: scan.id, itemLabel: scan.item_label, decision: scan.decision };
  }, [scan?.id, scan?.item_label, scan?.decision]);

  useEffect(() => {
    if (scan && navigator.vibrate) {
      // Haptic pattern by verdict
      if (scan.decision === 'GO') navigator.vibrate([40, 60, 40]);
      else if (scan.decision === 'MAYBE') navigator.vibrate(80);
      else navigator.vibrate([100, 40, 100]);
    }
    if (scan) {
      const t = setTimeout(() => setShowFlash(false), 850);
      return () => clearTimeout(t);
    }
  }, [scan]);

  if (loading) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-qc-green border-t-transparent mx-auto mb-4" />
          <p className="text-qc-text-dim text-sm">Loading result...</p>
        </div>
      </div>
    );
  }

  if (!scan) {
    return (
      <div className="min-h-screen bg-qc-bg flex items-center justify-center p-6">
        <div className="text-center">
          <p className="text-qc-text mb-4 font-bold">Scan not found</p>
          <Button
            onClick={() => navigate('/home')}
            className="bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-full px-6 font-extrabold"
          >
            Go Home
          </Button>
        </div>
      </div>
    );
  }

  const config = verdictConfig[scan.decision] || verdictConfig['MAYBE'];
  const Icon = config.Icon;

  return (
    <div className="min-h-screen bg-qc-bg text-qc-text relative" data-testid="result-screen">
      {/* Milestone celebration overlay */}
      {showMilestone && milestoneData?.milestone && (
        <MilestoneCelebration
          milestone={milestoneData.milestone}
          onDismiss={() => setShowMilestone(false)}
          delay={950}
        />
      )}
      {/* Full-screen verdict flash overlay */}
      {showFlash && (
        <div
          data-testid="verdict-flash"
          className={`fixed inset-0 z-50 ${config.flashClass} flex flex-col items-center justify-center pointer-events-none`}
          style={{ animation: 'flash 850ms ease-out forwards' }}
        >
          <div className="animate-verdict-pop flex flex-col items-center">
            <div className={`w-24 h-24 rounded-full bg-black/15 flex items-center justify-center mb-5 backdrop-blur-sm`}>
              <Icon className={config.textColor} style={{ width: '64px', height: '64px' }} strokeWidth={3} />
            </div>
            <h1
              className={`text-7xl sm:text-8xl font-black tracking-tighter ${config.textColor}`}
              style={{ textShadow: '0 4px 24px rgba(0,0,0,0.25)' }}
            >
              {config.label}
            </h1>
          </div>
        </div>
      )}

      {/* Header */}
      <div
        className="px-5 py-3 flex items-center sticky top-0 bg-qc-bg/80 backdrop-blur-md z-30 border-b border-qc-border"
        style={{ paddingTop: 'max(0.75rem, env(safe-area-inset-top, 0px))' }}
      >
        <button
          onClick={() => navigate('/home')}
          data-testid="back-button"
          className="w-10 h-10 flex items-center justify-center rounded-full bg-qc-surface border border-qc-border text-qc-text tappable"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <h1 className="text-base font-extrabold text-qc-text ml-3">Verdict</h1>
      </div>

      <div className="max-w-lg mx-auto px-5 pt-6 pb-32 space-y-4">
        {/* Verdict big card */}
        <div
          data-testid="verdict-card"
          className={`relative rounded-3xl border-2 ${config.borderSoft} ${config.bgSoft} p-6 text-center overflow-hidden animate-card-flip ${scan.decision === 'GO' ? 'animate-green-pulse' : ''}`}
        >
          {scan.item_label && (
            <p className="text-sm font-bold text-qc-text-dim uppercase tracking-wider mb-3" data-testid="item-label">
              {scan.item_label}
            </p>
          )}
          <div className={`w-20 h-20 mx-auto rounded-full ${config.bgSoft} border-2 ${config.borderSoft} flex items-center justify-center mb-3`}>
            <Icon className={config.accent} style={{ width: '48px', height: '48px' }} strokeWidth={3} />
          </div>
          <h2
            data-testid="decision-text"
            className={`text-6xl sm:text-7xl font-black tracking-tighter ${config.accent} mb-2`}
          >
            {scan.decision}
          </h2>
          <span className={`inline-block text-xs font-extrabold uppercase tracking-wider px-3 py-1 rounded-full border ${config.borderSoft} ${config.accent}`}>
            {scan.confidence} confidence
          </span>

          {milestoneData?.streak_days > 0 && (
            <div
              className="mt-4 inline-flex items-center gap-1.5 bg-qc-bg/40 border border-qc-amber/40 text-qc-amber px-3 py-1.5 rounded-full text-xs font-bold ml-2"
              data-testid="result-streak-chip"
            >
              <Flame className="w-3.5 h-3.5 fill-qc-amber" />
              {milestoneData.streak_days}-day streak
            </div>
          )}
        </div>

        {/* Product image */}
        {(scan.thumbnail_url || scan.image_url) && (
          <div className="rounded-2xl overflow-hidden border border-qc-border bg-qc-surface" data-testid="product-image">
            <img
              src={scan.image_url || scan.thumbnail_url}
              alt={scan.item_label || 'Scanned item'}
              className="w-full max-h-64 object-cover"
            />
          </div>
        )}

        {/* Reasons */}
        {scan.reasons && scan.reasons.length > 0 && (
          <div className="bg-qc-surface border border-qc-border rounded-2xl p-5" data-testid="reasons-list">
            <h3 className="text-xs font-bold text-qc-text-dim uppercase tracking-wider mb-3">
              Why this verdict
            </h3>
            <ul className="space-y-3">
              {scan.reasons.slice(0, 3).map((reason, i) => (
                <li key={`${scan.id}-reason-${i}`} className="flex items-start gap-3">
                  <div className={`w-1.5 h-1.5 rounded-full ${config.accent.replace('text-', 'bg-')} mt-2 flex-shrink-0`} />
                  <p className="text-base text-qc-text leading-snug">{reason}</p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* What to look for instead */}
        <div className={`rounded-2xl p-5 border ${config.borderSoft} ${config.bgSoft}`} data-testid="suggestion">
          <h3 className={`text-xs font-bold uppercase tracking-wider mb-2 ${config.accent}`}>
            What to look for instead
          </h3>
          <p className="text-base text-qc-text leading-snug">
            {scan.suggestion || config.fallbackSuggestion}
          </p>
        </div>
      </div>

      {/* Fixed bottom action */}
      <div
        className="fixed bottom-0 left-0 right-0 bg-gradient-to-t from-qc-bg via-qc-bg/95 to-transparent pt-6 pb-2 px-5 z-40"
        style={{ paddingBottom: 'max(1rem, env(safe-area-inset-bottom, 0px))' }}
      >
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <Button
            onClick={() => navigate('/scan')}
            data-testid="scan-another-button"
            className="capture-btn flex-1 h-16 bg-qc-green hover:bg-qc-green-dark text-qc-bg rounded-2xl font-black text-lg tracking-tight shadow-[0_10px_40px_rgba(0,230,118,0.35)] flex items-center justify-center gap-3"
          >
            <Camera className="w-6 h-6" strokeWidth={2.5} />
            Scan another
          </Button>
          <FavoriteButton scanId={scan.id} size="lg" />
          <ShareButton
            kind="scan"
            payload={sharePayload}
            testId="scan-share-button"
            compact
          />
        </div>
      </div>
    </div>
  );
}
